<template>
  <div
    :modelValue="modelValue"
    :class="{
      'flex-row': type === 'single' && options.length <= 2,
      'flex-col': multiple || options.length > 2,
    }"
    class="flex gap-1"
  >
    <Component
      v-for="option in formattedOptions"
      :key="option.id"
      :item="option"
      :class="{
        'text-center': type === 'single' && options.length <= 2,
        'flex-1': type === 'single' && options.length == 2,
      }"
      :is="isComponent"
      :checked="isChecked(option)"
      @click="handleUpdateModelValue(option)"
    />
    <Dialog
      @update:open="openDatePickerDialog = !openDatePickerDialog"
      :open="openDatePickerDialog"
      variant="default"
      size="default"
      side="bottom"
      :showCancel="false"
      :showConfirm="false"
    >
      <template #header>
        <DrawerHeader
          :title="label"
          v-bind="{
            variant: 'tertiary',
            type: 'gray',
            size: 'xl',
          }"
          class="bg-primary max-md:rounded-none border-b border-secondary"
        >
        </DrawerHeader>
      </template>
      <template #content>
        <div class="bg-primary overflow-y-auto flex flex-col gap-4 p-4">
          <DateRangePicker
            :range="modelValue.range"
            @rangeSelected="handleCustomRangeSelected"
          />
        </div>
      </template>
    </Dialog>
  </div>
  <div class="flex justify-between items-center">
    <div>
      <Button
        :label="t('components.buttons.clear')"
        :disabled="count === 0"
        @click="handleClear"
        size="lg"
        variant="link"
        class="mt-2"
        v-if="type !== 'single'"
        type="destructive"
      >
        <template #start>
          <Icon name="delete" />
        </template>
      </Button>
    </div>
    <div
      v-if="options.length > per_page && !showAll"
      class="flex justify-center align-items-center"
    >
      <Button
        :label="t('components.buttons.show_more')"
        @click="loadMore"
        size="lg"
        class="mt-2"
        variant="link"
        type="gray"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, ref, toRef } from "vue";
import { useDate } from "@/Composables/useDate";

import { Button } from "@/SharedComponents/Buttons";
import { Dialog } from "@/SharedComponents/Dialog";
import { DrawerHeader } from "@/SharedComponents/Drawer";

import { useFilterGroup } from "@/Composables/useFilterGroup";
import {
  Avatar,
  Default,
  Checkbox,
  Dot,
} from "@/SharedComponents/Filters/Drawer/Accordion/Inputs";
import { DateShortcut, FilterType } from "@/SharedComponents/Filters";
import { DateRangePicker } from "@/SharedComponents/Date";
import { useI18n } from "vue-i18n";
import { useArrayToggle } from "@/Utils/arrayToggle";
import { Icon } from "@/SharedComponents/Icon";

const { t } = useI18n();
const { formatDate } = useDate();

const props = defineProps({
  handle: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    required: true,
  },
  multiple: {
    type: Boolean,
    required: true,
  },
  expanded: {
    type: Boolean,
    required: false,
    default: false,
  },
  type: {
    type: String,
    required: true,
  },
  modelValue: {
    type: Array,
    default() {
      return [];
    },
  },
  per_page: {
    type: Number,
    required: false,
  },
  options: {
    type: Array,
    required: true,
  },
  count: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(["update:modelValue"]);
const openDatePickerDialog = ref(false);

const modelValue = ref(props.modelValue);

const { toggleArrayItem } = useArrayToggle();

const isComponent = computed(() => {
  switch (props.type) {
    case "checkbox":
      return Checkbox;
    case "avatar":
      return Avatar;
    case "dot":
      return Dot;
    case "single":
    default:
      return Default;
  }
});

const handleUpdateModelValue = (option) => {
  switch (props.type) {
    case "single":
      handleSingleSelector(option);
      break;
    case "date":
      handleDateSelector(option);
      break;
    default:
      handleMultiSelector(option);
      break;
  }
};
const handleCustomRangeSelected = (value) => {
  emit("update:modelValue", {
    values: {
      shortcutId: DateShortcut.CUSTOM,
      range: value,
    },
    handle: props.handle,
  });
  openDatePickerDialog.value = false;
};

const handleDateSelector = (option) => {
  if (option.id === DateShortcut.CUSTOM) {
    if (props.modelValue.shortcutId !== DateShortcut.CUSTOM) {
      emit("update:modelValue", { values: {}, handle: props.handle });
    }
    openDatePickerDialog.value = true;
    return;
  }
  emit("update:modelValue", {
    values: {
      shortcutId: option.id,
      label: option.label,
      range: option.range(),
    },
    handle: props.handle,
  });
};

const handleMultiSelector = (option) => {
  const values = [...props.modelValue];
  if (props.handle === FilterType.CATEGORY && option.ids) {
    option.ids.forEach((id) => toggleArrayItem(values, id));
  } else {
    toggleArrayItem(values, option.id);
  }
  emit("update:modelValue", { values, handle: props.handle });
};

const handleSingleSelector = (option) => {
  const values = props.modelValue === option.id ? [] : [option.id];
  emit("update:modelValue", { values: values, handle: props.handle });
};

const handleClear = () => {
  emit("update:modelValue", { values: [], handle: props.handle });
};

const isChecked = (option) => {
  if (props.type === "date") {
    return props.modelValue.shortcutId === option.id;
  }

  if (props.handle === FilterType.CATEGORY && option.ids) {
    return option.ids.some((id) => props.modelValue.includes(id));
  }

  if (props.type === "single") {
    return props.modelValue === option.id;
  }

  return props.modelValue.includes(option.id);
};

const { allOptions, loadMore, showAll } = useFilterGroup(
  toRef(() => ({
    options: props.options,
    multiple: props.multiple,
    per_page: props.per_page,
    modelValue: modelValue.value,
  }))
);

const formattedOptions = computed(() => {
  if (props.type !== "date") {
    return allOptions.value;
  }

  return allOptions.value.map((option) => {
    if (option.id === DateShortcut.CUSTOM) {
      const hasRange =
        props.modelValue.range &&
        props.modelValue.range.start &&
        props.modelValue.range.end;

      const dateLabel = hasRange
        ? `${formatDate(props.modelValue.range.start)} - ${formatDate(
            props.modelValue.range.end
          )}`
        : "";
      const defaultLabel = t("components.inputs.datepicker.shortcuts.custom");

      option.label =
        props.modelValue.shortcutId === DateShortcut.CUSTOM
          ? dateLabel || defaultLabel
          : defaultLabel;
    }
    return option;
  });
});
</script>
